import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { io } from "socket.io-client";
import PopupAlert from "../components/PopupAlert";
import Toast from "../components/Toast";

const GocLeadsComponent = ({ role, dataAccessDays, logout }) => {
  const link = "https://server.garageoncall.com";
  // const link = "http://localhost:7001";
  // const link = "http://192.168.1.4:7001";

  const [bookingId, setBookingId] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [deleteBooking, setDeleteBooking] = useState(null);
  const [newBookings, setNewBookings] = useState([]);
  const [data, setData] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [bookingData, setBookingData] = useState({});
  const [page, setPage] = useState(0);
  const itemsPerPage = 50;

  const audioRefNew = useRef(new Audio("/sounds/notification1.mp3"));
  const audioRefUpdate = useRef(new Audio("/sounds/notification.mp3"));
  const socketRef = useRef(null);

  // Fetch data function wrapped with useCallback to avoid recreation
  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${link}/bookings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization:
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3Mzc5ODEzMzl9.6TUusoEUakeLbVVZ3NlPQJoSH1sGoSzsS2hFwS2gzPI",
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        setData(responseData.data.reverse());
      } else {
        console.error("Error fetching bookings");
      }
    } catch (error) {
      console.error("Server error:", error);
    }
  }, [link]);

  // Play audio safely
  const playSound = (audioRef) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch((err) => {
      console.error("Playback error:", err);
    });
  };

  // Setup socket connection and listeners once on mount
  useEffect(() => {
    socketRef.current = io(link);

    socketRef.current.on("new-booking", (booking) => {
      setData((prev) => [booking, ...prev]);
      setNewBookings((prev) => [...prev, booking]);

      if (Notification.permission === "granted") {
        new Notification("Garage On Call - Booking", {
          body: `${booking.name ? `Name: ${booking.name}` : ""}\nNumber: ${
            booking.phone
          }${booking.address ? `\nLocation: ${booking.address}` : ""}`,
        });
      }
      playSound(audioRefNew);
    });

    socketRef.current.on("booking-update", () => {
      playSound(audioRefUpdate);
      fetchData();
      setShowToast(true);
      setEditBooking(null);
    });

    socketRef.current.on("delete-booking", () => {
      playSound(audioRefUpdate);
      fetchData();
      setShowToast(true);
      setEditBooking(null);
    });

    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }

    fetchData();

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.off("new-booking");
        socketRef.current.off("booking-update");
        socketRef.current.off("delete-booking");
        socketRef.current.disconnect();
      }
    };
  }, [fetchData, link]);

  // Filter data based on role and dataAccessDays
  const filteredData = React.useMemo(() => {
    if (role === "admin") return data;

    if (role === "user") {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - dataAccessDays);
      return data.filter(
        (e) =>
          new Date(e.date).toISOString().split("T")[0] >
          startDate.toISOString().split("T")[0]
      );
    }

    logout();
    return [];
  }, [data, role, dataAccessDays, logout]);

  const totalItems = filteredData.length;
  const startIndex = page * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const pageData = filteredData.slice(startIndex, endIndex);

  // Pagination handlers
  const handleNext = () => {
    if (endIndex < totalItems) setPage((p) => p + 1);
  };
  const handlePrev = () => {
    if (page > 0) setPage((p) => p - 1);
  };

  // Input handler for editing booking
  const handleInput = (e) => {
    setBookingData({
      ...bookingData,
      [e.target.name]: e.target.value,
    });
  };

  // Update booking handler
  const handleUpdate = async () => {
    try {
      const response = await fetch(`${link}/bookings/${bookingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization:
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3Mzc5ODEzMzl9.6TUusoEUakeLbVVZ3NlPQJoSH1sGoSzsS2hFwS2gzPI",
        },
        body: JSON.stringify(bookingData),
      });

      if (response.ok) {
        setShowToast(true);
        setEditBooking(false);
        fetchData();
      } else {
        alert("Error updating booking");
      }
    } catch (error) {
      alert("Server error");
    }
  };

  // Delete booking handler
  const handleDelete = async () => {
    try {
      const response = await fetch(`${link}/bookings/${bookingId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          authorization:
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3Mzc5ODEzMzl9.6TUusoEUakeLbVVZ3NlPQJoSH1sGoSzsS2hFwS2gzPI",
        },
      });

      if (response.ok) {
        setShowToast(true);
        setEditBooking(false);
        setDeleteBooking(false);
        fetchData();
      } else {
        alert("Error deleting booking");
      }
    } catch (error) {
      alert("Server error");
    }
  };

  //   :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

  const copyLead = async (e) => {
    const text = `${e.name ? `Name : ${e.name}` : ""}${
      e.phone ? `\nPhone : ${e.phone}` : ""
    }${e.address ? `\nAddress : ${e.address}` : ""}${
      e.bikeBrand ? `\nBike Brand : ${e.bikeBrand}` : ""
    }${e.bikeModel ? `\nBike Model : ${e.bikeModel}` : ""}${
      e.bikeCC ? `\nBike CC : ${e.bikeCC}` : ""
    }${e.displayCharges ? `\nCharges : Rs.${e.displayCharges}/-` : ""}${
      e.serviceRequired ? `\nService Required : ${e.serviceRequired}` : ""
    }`;

    try {
      await navigator.clipboard.writeText(text);
      setShowCopyToast(true);
    } catch (err) {
      alert("Failed to copy!");
    }
  };

  const whatsappLeadText = (e) => {
    const text = `${e.name ? `Name : ${e.name}` : ""}${
      e.phone ? `%0APhone : ${e.phone}` : ""
    }${e.address ? `%0AAddress : ${e.address}` : ""}${
      e.bikeBrand ? `%0ABike Brand : ${e.bikeBrand}` : ""
    }${e.bikeModel ? `%0ABike Model : ${e.bikeModel}` : ""}${
      e.bikeCC ? `%0ABike CC : ${e.bikeCC}` : ""
    }${e.displayCharges ? `%0ACharges : Rs.${e.displayCharges}/-` : ""}${
      e.serviceRequired ? `%0AService Required : ${e.serviceRequired}` : ""
    }`;

    return text;
  };

  //   :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

  const shareText = (e) => {
    const text = `${e.name ? `Name : ${e.name}` : ""}${
      e.phone ? `\nPhone : ${e.phone}` : ""
    }${e.address ? `\nAddress : ${e.address}` : ""}${
      e.bikeBrand ? `\nBike Brand : ${e.bikeBrand}` : ""
    }${e.bikeModel ? `\nBike Model : ${e.bikeModel}` : ""}${
      e.bikeCC ? `\nBike CC : ${e.bikeCC}` : ""
    }${e.displayCharges ? `\nCharges : Rs.${e.displayCharges}/-` : ""}${
      e.serviceRequired ? `\nService Required : ${e.serviceRequired}` : ""
    }`;

    navigator.share
      ? navigator.share({ text: text })
      : alert("Web Share API not supported");
  };

  //   :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

  return (
    <>
      {deleteBooking ? (
        <div className="dboard-log-out-alert-n">
          <PopupAlert
            heading="You want to delete booking. Are you sure ?"
            icon="fa-solid fa-trash"
            open={setDeleteBooking}
            action={handleDelete}
          />
        </div>
      ) : (
        ""
      )}

      {showCopyToast ? (
        <Toast heading="Lead Copied Sucessfully" open={setShowCopyToast} />
      ) : (
        ""
      )}

      {showToast ? (
        <Toast heading="Data Updated Sucessfully" open={setShowToast} />
      ) : (
        ""
      )}
      {newBookings.length > 0 ? (
        <div className="goc-dashbord-new-leads">
          <div className="goc-dashbord-new-leads-main">
            <div className="goc-dashbord-new-leads-head">
              <div>{newBookings.length} New Booking </div>
              <span onClick={() => setNewBookings([])}>clear</span>
            </div>

            {newBookings.length > 0 ? (
              newBookings.map((e) => (
                <div className="goc-dashbord-new-leads-list">
                  <div>
                    <div>{e.phone}</div>
                    <div>{e.name && e.name}</div>
                  </div>
                  <div>
                    <div> {new Date(e.date).toDateString()} </div>
                    <div> {new Date(e.date).toLocaleTimeString()} </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="goc-dashbord-new-leads-list">
                <b>No New Bookings</b>
              </div>
            )}
          </div>
        </div>
      ) : (
        ""
      )}

      <div className="goc-dashbord-leads-list-cotainer">
        <div className="goc-dashbord-leads-list-cotainer-header">
          <b>All Bookings</b>
          <div className="goc-dashbord-leads-list-cotainer-header-filter">
            <div className="goc-dashbord-leads-list-cotainer-header-filter-buttons">
              {page > 0 && (
                <i onClick={handlePrev} className="fa-solid fa-angle-left"></i>
              )}
              <div>
                {startIndex + 1}–{endIndex} of {totalItems}
              </div>
              {endIndex < totalItems && (
                <i onClick={handleNext} className="fa-solid fa-angle-right"></i>
              )}
            </div>
          </div>
        </div>

        <div className="goc-dashbord-leads-list-inner-cotainer">
          <div className="goc-dashbord-leads-list-inner-header">
            <div>Details</div>
            <div>Date Time</div>
            <div>Address</div>
            <div>Model Brand</div>
            <div>Edit</div>
          </div>

          {/* sm-list  Header::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::: */}
          {/*::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::  */}

          <div className="wraper-goc-dashbord-leads-booking-main-small-header">
            <div className="wraper-goc-dashbord-leads-booking-small-header">
              <div>Details</div>
              <div>Date Time</div>
            </div>
          </div>

          {pageData &&
            pageData.map((e, i) => (
              <>
                <div className="goc-dashbord-leads-booking-list">
                  <div
                    className="goc-dashbord-leads-booking-list-data"
                    onClick={() => {
                      setBookingId(bookingId === e._id ? null : e._id);
                      setEditBooking(null);
                    }}
                  >
                    <div className="goc-leads-booking-list-col">
                      <div>{e.phone}</div>
                      {e.name ? <div>{e.name}</div> : ""}
                    </div>
                    <div className="goc-leads-booking-list-col">
                      <div> {new Date(e.date).toDateString()} </div>
                      <div> {new Date(e.date).toLocaleTimeString()} </div>
                    </div>
                    <div className="goc-leads-booking-list-col">
                      <div>{e.address ? e.address : "-"}</div>
                    </div>
                    <div className="goc-leads-booking-list-col">
                      <div>{e.bikeBrand ? e.bikeBrand : "--"}</div>
                      <div>{e.bikeModel}</div>
                    </div>

                    <div className="goc-leads-booking-list-col"></div>
                  </div>

                  <div className="goc-leads-booking-list-col">
                    <div className="goc-dashbord-share-leads-btn">
                      <Link
                        target="_blank"
                        to={`https://web.whatsapp.com/send?text=${whatsappLeadText(
                          e
                        )}`}
                      >
                        <i class="fa-brands fa-whatsapp"></i>
                      </Link>
                      <i
                        class="fa-solid fa-copy"
                        onClick={() => copyLead(e)}
                      ></i>
                      <i
                        onClick={() => shareText(e)}
                        class="fa-solid fa-share"
                      ></i>
                    </div>
                  </div>

                  {bookingId === e._id ? (
                    <div className="goc-dashbord-lead-booking-full-details">
                      <div className="goc-dashbord-lead-booking-full-details-head">
                        <div className="goc-dashbord-lead-booking-full-details-heading">
                          Full Details :
                        </div>
                        <div className="goc-dashbord-lead-booking-full-details-options">
                          {editBooking ? (
                            <>
                              <span onClick={handleUpdate}>
                                <i className="fa-solid fa-check-circle"></i>{" "}
                                save
                              </span>

                              <span onClick={() => setEditBooking(null)}>
                                <i className="fa-solid fa-xmark-circle"></i>{" "}
                                cancle
                              </span>
                            </>
                          ) : (
                            <>
                              <span
                                onClick={() => {
                                  setEditBooking(true);
                                  setBookingData(e);
                                }}
                              >
                                <i className="fa-solid fa-pen"></i> edit
                              </span>
                              {role === "admin" ? (
                                <span onClick={() => setDeleteBooking(true)}>
                                  <i className="fa-solid fa-trash"></i> delete
                                </span>
                              ) : (
                                ""
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <Link target="_blank" to={e.formUrl}>
                        {e.formUrl}
                      </Link>

                      {editBooking ? (
                        <div className="goc-dashbord-lead-booking-more-details">
                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>Name :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.name}
                              name="name"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>phone :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.phone}
                              name="phone"
                              type="number"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>bikeBrand :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.bikeBrand}
                              name="bikeBrand"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>bikeModel :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.bikeModel}
                              name="bikeModel"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>bikeCC :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.bikeCC}
                              name="bikeCC"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>Charges :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.displayCharges}
                              name="displayCharges"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>Service :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.serviceRequired}
                              name="serviceRequired"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>address :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.address}
                              name="address"
                              type="text"
                            />
                          </div>

                          {e.subject ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>subject :</div>
                              <input
                                onChange={handleInput}
                                value={bookingData.subject}
                                name="subject"
                                type="text"
                              />
                            </div>
                          ) : (
                            ""
                          )}

                          {e.message ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>message :</div>
                              <input
                                onChange={handleInput}
                                value={bookingData.message}
                                name="message"
                                type="text"
                              />
                            </div>
                          ) : (
                            ""
                          )}

                          {e.formType ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>formType :</div>
                              <span>{e.formType}</span>
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        <div className="goc-dashbord-lead-booking-more-details">
                          {e.name ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>Name :</div>
                              <span>{e.name}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.phone ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>phone :</div>
                              <span>{e.phone}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.bikeBrand ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>bikeBrand :</div>
                              <span>{e.bikeBrand}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.bikeModel ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>bikeModel :</div>
                              <span>{e.bikeModel}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.bikeCC ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>bikeCC :</div>
                              <span>{e.bikeCC}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.displayCharges ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>Charges :</div>
                              <span>Rs. {e.displayCharges} /-</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.serviceRequired ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>Service :</div>
                              <span>{e.serviceRequired}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.address ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>address :</div>
                              <span>{e.address}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.subject ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>subject :</div>
                              <span>{e.subject}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.message ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>message :</div>
                              <span>{e.message}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.formType ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>formType :</div>
                              <span>{e.formType}</span>
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </>
            ))}

          {/* sm-list :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::: */}
          {/*::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::  */}

          {pageData &&
            pageData.map((e, i) => (
              <div className="goc-dashbord-leads-booking-small-list">
                <div className="goc-dashbord-leads-booking-small-inner-list">
                  <div className="sm-ds-list-number-postion">
                    <Link to={`tel:${e.phone}`}>{e.phone}</Link>
                  </div>
                  <div
                    onClick={() => {
                      setBookingId(bookingId === e._id ? null : e._id);
                      setEditBooking(null);
                    }}
                  >
                    <div className="wraper-goc-dashbord-leads-booking-small-inner-list">
                      <div>
                        <div className="goc-leads-booking-list-col   sm-goc-leads-booking-list-col">
                          <div> &nbsp; </div>
                          {e.name ? <div>{e.name}</div> : ""}
                        </div>
                      </div>
                      <div>
                        <div>
                          <div>{new Date(e.date).toDateString()}</div>
                          <div>{new Date(e.date).toLocaleTimeString()}</div>
                        </div>
                      </div>
                    </div>
                    <div className="goc-dashbod-lead-sm-booking-address-list">
                      {e.address ? <div>{e.address}</div> : ""}
                    </div>
                  </div>

                  {bookingId === e._id ? (
                    <div className="goc-dashbord-lead-booking-full-details">
                      <div className="goc-dashbord-lead-booking-full-details-head">
                        <div className="goc-dashbord-lead-booking-full-details-heading">
                          Full Details :
                        </div>
                        <div className="goc-dashbord-lead-booking-full-details-options">
                          {editBooking ? (
                            <>
                              <span onClick={handleUpdate}>
                                <i className="fa-solid fa-check-circle"></i>{" "}
                                save
                              </span>

                              <span onClick={() => setEditBooking(null)}>
                                <i className="fa-solid fa-xmark-circle"></i>{" "}
                                cancle
                              </span>
                            </>
                          ) : (
                            <>
                              <span
                                onClick={() => {
                                  setEditBooking(true);
                                  setBookingData(e);
                                }}
                              >
                                <i className="fa-solid fa-pen"></i> edit
                              </span>
                              {role === "admin" ? (
                                <span onClick={() => setDeleteBooking(true)}>
                                  <i className="fa-solid fa-trash"></i> delete
                                </span>
                              ) : (
                                ""
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <Link target="_blank" to={e.formUrl}>
                        {e.formUrl}
                      </Link>

                      {editBooking ? (
                        <div className="goc-dashbord-lead-booking-more-details">
                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>Name :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.name}
                              name="name"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>phone :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.phone}
                              name="phone"
                              type="number"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>bikeBrand :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.bikeBrand}
                              name="bikeBrand"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>bikeModel :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.bikeModel}
                              name="bikeModel"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>bikeCC :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.bikeCC}
                              name="bikeCC"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>Charges :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.displayCharges}
                              name="displayCharges"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>Service :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.serviceRequired}
                              name="serviceRequired"
                              type="text"
                            />
                          </div>

                          <div className="goc-dashbord-lead-booking-more-details-list">
                            <div>address :</div>
                            <input
                              onChange={handleInput}
                              value={bookingData.address}
                              name="address"
                              type="text"
                            />
                          </div>

                          {e.subject ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>subject :</div>
                              <input
                                onChange={handleInput}
                                value={bookingData.subject}
                                name="subject"
                                type="text"
                              />
                            </div>
                          ) : (
                            ""
                          )}

                          {e.message ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>message :</div>
                              <input
                                onChange={handleInput}
                                value={bookingData.message}
                                name="message"
                                type="text"
                              />
                            </div>
                          ) : (
                            ""
                          )}

                          {e.formType ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>formType :</div>
                              <span>{e.formType}</span>
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        <div className="goc-dashbord-lead-booking-more-details">
                          {e.name ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>Name :</div>
                              <span>{e.name}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.phone ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>phone :</div>
                              <span>{e.phone}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.bikeBrand ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>bikeBrand :</div>
                              <span>{e.bikeBrand}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.bikeModel ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>bikeModel :</div>
                              <span>{e.bikeModel}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.bikeCC ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>bikeCC :</div>
                              <span>{e.bikeCC}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.displayCharges ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>Charges :</div>
                              <span>Rs. {e.displayCharges} /-</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.serviceRequired ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>Service :</div>
                              <span>{e.serviceRequired}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.address ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>address :</div>
                              <span>{e.address}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.subject ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>subject :</div>
                              <span>{e.subject}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.message ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>message :</div>
                              <span>{e.message}</span>
                            </div>
                          ) : (
                            ""
                          )}

                          {e.formType ? (
                            <div className="goc-dashbord-lead-booking-more-details-list">
                              <div>formType :</div>
                              <span>{e.formType}</span>
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </div>

                <div className="goc-dashbord-sm-share-leads-btn">
                  <Link
                    target="_blank"
                    to={`https://wa.me/?text=${whatsappLeadText(e)}`}
                  >
                    <i class="fa-brands fa-whatsapp"></i>
                  </Link>
                  <i class="fa-solid fa-copy" onClick={() => copyLead(e)}></i>
                  <i onClick={() => shareText(e)} class="fa-solid fa-share"></i>
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default GocLeadsComponent;
